import React, { useEffect, useState } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';

/**
 * System Administration → Email Settings.
 *
 * Allows an admin to configure SMTP relay for outbound mail. Defaults to
 * Office 365 (smtp.office365.com:587, STARTTLS). Passwords are encrypted
 * at rest on the server — the form never reads them back.
 */
export default function EmailSettings({ currentUser }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState(currentUser?.email || '');

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch('/api/admin/mail/config');
        if (r.ok) {
          const { config: c } = await r.json();
          setConfig(c);
          if (c) setForm(f => ({ ...f, ...c, password: '' }));
        }
      } catch (_) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const body = { ...form };
      // Only forward secrets if the admin actually typed something — empty
      // strings would clobber what's stored. The server preserves existing
      // *_enc values when these keys are absent.
      if (!body.password) delete body.password;
      if (!body.client_secret) delete body.client_secret;
      const r = await apiFetch('/api/admin/mail/config', { method: 'PUT', body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success('Email settings saved');
        setConfig(d.config || null);
        setForm(f => ({ ...f, password: '', client_secret: '' }));
      } else {
        toast.error(d.error || 'Save failed');
      }
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  }

  async function sendTest() {
    if (!testTo) { toast.error('Enter a recipient email'); return; }
    setTesting(true);
    try {
      const r = await apiFetch('/api/admin/mail/test', { method: 'POST', body: JSON.stringify({ to: testTo }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) toast.success(d.message || 'Test email sent');
      else toast.error(d.error || 'Test failed');
    } catch (e) { toast.error(e.message); }
    setTesting(false);
  }

  function applyProvider(p) {
    if (p === 'o365') {
      setForm(f => ({ ...f, provider: 'o365', host: 'smtp.office365.com', port: 587, secure: false }));
    } else if (p === 'gmail') {
      setForm(f => ({ ...f, provider: 'smtp', host: 'smtp.gmail.com', port: 587, secure: false }));
    } else if (p === 'graph') {
      // Microsoft Graph — OAuth2 client credentials, no SMTP fields needed.
      // We KEEP `username` (mailbox UPN to send AS) and clear SMTP-only fields
      // so the form's status pill doesn't get confused.
      setForm(f => ({ ...f, provider: 'graph' }));
    } else {
      setForm(f => ({ ...f, provider: 'smtp' }));
    }
  }

  // Graph mode toggles a different field set (no host/port/SSL, but adds
  // Tenant / Client ID / Client Secret). isGraph centralises that branch.
  const isGraph = (form.provider || '').toLowerCase() === 'graph';

  if (loading) return <div style={{ padding: 40, color: '#94A3B8' }}>Loading…</div>;

  return (
    <div style={{ padding: 20, maxWidth: 920, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 18 }}>Email Settings</h1>
            {/* Status pill — green when SMTP is fully configured (host + username
                + password saved + enabled), amber when partially configured,
                grey when nothing's set up yet. Lets the admin see at a glance
                whether outbound mail is actually wired up. */}
            <EmailStatusPill config={config} form={form} />
          </div>
          <div style={{ color: '#94A3B8', fontSize: 12.5, marginTop: 4 }}>
            Outbound mail relay. Pick <strong style={{ color: '#6FE6FF' }}>Microsoft Graph</strong> for
            future-proof OAuth-based sending, or stick with SMTP if you have App Passwords + SMTP AUTH already working.
            {config?.password_set && <span style={{ marginLeft: 8, color: '#10B981' }}>· SMTP password saved</span>}
            {config?.client_secret_set && <span style={{ marginLeft: 8, color: '#10B981' }}>· Graph secret saved</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      <Section title="Provider">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            ['graph', 'Microsoft Graph (recommended)'],
            ['o365', 'Microsoft 365 / Exchange Online (SMTP)'],
            ['gmail', 'Gmail / Google Workspace (SMTP)'],
            ['smtp', 'Custom SMTP'],
          ].map(([v, lbl]) => (
            <button key={v} onClick={() => applyProvider(v)} style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
              border: '1px solid ' + (form.provider === v ? 'rgba(0,224,255,0.55)' : 'rgba(255,255,255,0.10)'),
              background: form.provider === v ? 'rgba(0,224,255,0.14)' : 'rgba(255,255,255,0.04)',
              color: form.provider === v ? '#6FE6FF' : '#E5E7EB',
            }}>{lbl}</button>
          ))}
        </div>
        {isGraph ? (
          <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 10, lineHeight: 1.55 }}>
            <strong style={{ color: '#94A3B8' }}>Microsoft Graph setup:</strong> in
            Entra → <em>App registrations</em> → New registration. Under{' '}
            <em>API permissions</em> add <code>Mail.Send</code> (Application
            permission, NOT Delegated) and grant admin consent. Under{' '}
            <em>Certificates &amp; secrets</em> create a Client Secret and copy
            its <em>Value</em>. Optional but recommended: scope the app to a single
            mailbox via <code>New-ApplicationAccessPolicy</code> in PowerShell so
            the app can ONLY send as the mailbox below — not every mailbox in your
            tenant. No SMTP AUTH, no App Password — survives Microsoft's continued
            tightening of legacy auth.
          </div>
        ) : (
          <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 10 }}>
            <strong style={{ color: '#94A3B8' }}>For Microsoft 365 (SMTP):</strong> create an <em>App Password</em> for your
            account, paste below, and ensure SMTP AUTH is enabled for the mailbox.
            <strong style={{ color: '#94A3B8', marginLeft: 6 }}>Note:</strong> Microsoft is phasing out SMTP AUTH —
            switch to <em>Microsoft Graph</em> above for a future-proof setup.
          </div>
        )}
      </Section>

      {!isGraph && (
        <Section title="Transport">
          <div style={grid2}>
            <Field label="Host"><input value={form.host || ''} onChange={e => set('host', e.target.value)} style={inp} placeholder="smtp.office365.com" /></Field>
            <Field label="Port">
              <input type="number" value={form.port ?? 587}
                onChange={e => {
                  // Auto-align secure with the port choice — 465 = implicit TLS, anything else (587, 25, 2525) = STARTTLS.
                  const p = parseInt(e.target.value, 10) || 0;
                  setForm(f => ({ ...f, port: p, secure: p === 465 }));
                }}
                style={inp} />
            </Field>
            <Field label="Secure (SSL)">
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
                <input type="checkbox" checked={!!form.secure}
                  onChange={e => {
                    // Toggling Secure flips the port too so the two stay consistent.
                    // Port 465 ↔ Secure on. 587 ↔ Secure off.
                    const sec = e.target.checked;
                    setForm(f => ({ ...f, secure: sec, port: sec ? 465 : 587 }));
                  }} />
                <span style={{ fontSize: 12.5, color: '#94A3B8' }}>465 = implicit SSL · 587 = STARTTLS (recommended for O365)</span>
              </label>
            </Field>
            <Field label="Enabled">
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
                <input type="checkbox" checked={form.enabled !== false} onChange={e => set('enabled', e.target.checked)} />
                <span style={{ fontSize: 12.5, color: '#94A3B8' }}>Send live emails</span>
              </label>
            </Field>
          </div>
        </Section>
      )}

      {isGraph ? (
        // Graph credentials — Tenant ID, Client ID, Client Secret. The Username
        // field stays here because Graph still needs to know which mailbox to
        // send AS (the /users/{upn}/sendMail URL).
        <Section title="Microsoft Graph credentials">
          <div style={grid2}>
            <Field label="Directory (Tenant) ID">
              <input value={form.ms_tenant_id || ''} onChange={e => set('ms_tenant_id', e.target.value)} style={inp}
                placeholder="00000000-0000-0000-0000-000000000000" />
            </Field>
            <Field label="Application (Client) ID">
              <input value={form.client_id || ''} onChange={e => set('client_id', e.target.value)} style={inp}
                placeholder="00000000-0000-0000-0000-000000000000" />
            </Field>
            <Field label="Client Secret (Value, not Secret ID)">
              <input type="password" value={form.client_secret || ''} onChange={e => set('client_secret', e.target.value)} style={inp}
                placeholder={config?.client_secret_set ? '•••••••• (saved — type to change)' : 'Paste secret value'} />
            </Field>
            <Field label="Mailbox to send AS (UPN)">
              <input value={form.username || ''} onChange={e => set('username', e.target.value)} style={inp}
                placeholder="nouveau.erp@thenewequation.biz" />
            </Field>
            <Field label="Enabled" wide>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
                <input type="checkbox" checked={form.enabled !== false} onChange={e => set('enabled', e.target.checked)} />
                <span style={{ fontSize: 12.5, color: '#94A3B8' }}>Send live emails</span>
              </label>
            </Field>
          </div>
        </Section>
      ) : (
        <Section title="Authentication">
          <div style={grid2}>
            <Field label="Username (mailbox address)"><input value={form.username || ''} onChange={e => set('username', e.target.value)} style={inp} placeholder="notifications@univerk.ai" /></Field>
            <Field label="Password / App password">
              <input type="password" value={form.password || ''} onChange={e => set('password', e.target.value)} style={inp}
                placeholder={config?.password_set ? '•••••••• (saved — type to change)' : 'Enter password'} />
            </Field>
          </div>
        </Section>
      )}

      <Section title="From / Reply">
        <div style={grid2}>
          <Field label="From email (defaults to Username)"><input value={form.from_email || ''} onChange={e => set('from_email', e.target.value)} style={inp} placeholder="noreply@univerk.ai" /></Field>
          <Field label="From name"><input value={form.from_name || ''} onChange={e => set('from_name', e.target.value)} style={inp} placeholder="BizSuite Notifications" /></Field>
          <Field label="Reply-To (optional)" wide><input value={form.reply_to || ''} onChange={e => set('reply_to', e.target.value)} style={inp} placeholder="hr@univerk.ai" /></Field>
        </div>
      </Section>

      <Section title="Send a test email">
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="To">
            <input value={testTo} onChange={e => setTestTo(e.target.value)} style={{ ...inp, width: 260 }} />
          </Field>
          <button onClick={sendTest} disabled={testing} style={btnPrimary}>{testing ? 'Sending…' : 'Send test'}</button>
        </div>
        <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 8 }}>
          Saves the current settings first by clicking <strong>Save</strong> above, then sends a short test message.
          Errors from the relay (bad password, blocked port, etc.) are shown as toast notifications.
        </div>
      </Section>
    </div>
  );
}

const EMPTY = {
  provider: 'o365', host: 'smtp.office365.com', port: 587, secure: false,
  username: '', password: '',
  // Graph fields — left empty by default; picked up if provider switches to 'graph'
  ms_tenant_id: '', client_id: '', client_secret: '',
  from_email: '', from_name: 'BizSuite Reminders',
  reply_to: '', enabled: true,
};

/**
 * Pill that shows the live status of the email relay configuration.
 *   green  = fully configured + enabled
 *   amber  = partially configured (host or username missing) OR disabled
 *   grey   = nothing saved yet
 *
 * The "fully configured" check is provider-aware — SMTP needs host + user +
 * password, Graph needs tenant + client_id + client_secret + user.
 */
function EmailStatusPill({ config, form }) {
  const enabled = (form && form.enabled !== false);
  const hasUser = !!(form && form.username);
  const isGraph = (form && (form.provider || '').toLowerCase() === 'graph');

  let fullyConfigured;
  if (isGraph) {
    const hasTenant = !!(form && form.ms_tenant_id);
    const hasClient = !!(form && form.client_id);
    const secretSaved = !!(config && config.client_secret_set);
    fullyConfigured = hasTenant && hasClient && hasUser && secretSaved;
  } else {
    const hasHost = !!(form && form.host);
    const passwordSaved = !!(config && config.password_set);
    fullyConfigured = hasHost && hasUser && passwordSaved;
  }

  let label, color, bg, border;
  const noConfigYet = !config && !hasUser
    && !(form && (form.host || form.ms_tenant_id || form.client_id));
  if (noConfigYet) {
    label = 'Not configured'; color = '#94A3B8'; bg = 'rgba(148,163,184,0.15)'; border = 'rgba(148,163,184,0.40)';
  } else if (fullyConfigured && enabled) {
    label = isGraph ? 'Graph · live' : 'Configured · live';
    color = '#10B981'; bg = 'rgba(16,185,129,0.15)'; border = 'rgba(16,185,129,0.40)';
  } else if (fullyConfigured && !enabled) {
    label = 'Configured · disabled'; color = '#F59E0B'; bg = 'rgba(245,158,11,0.15)'; border = 'rgba(245,158,11,0.40)';
  } else {
    label = 'Incomplete'; color = '#F59E0B'; bg = 'rgba(245,158,11,0.15)'; border = 'rgba(245,158,11,0.40)';
  }
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
      color, background: bg, border: `1px solid ${border}`, textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{label}</span>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children, wide }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: wide ? '1 / -1' : 'auto' }}>
      <span style={{ fontSize: 10.5, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}

const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 };
const inp = { padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 13, outline: 'none', width: '100%' };
const btnPrimary = { padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #00E0FF, #3B8FF4)', color: '#fff' };
